import { useEffect, useState } from "react";
import {
  fetchFontBytes,
  FONT_LIBRARY,
  isLibraryFamily,
  libraryEntry,
  pickVariant,
  type CustomFont,
} from "../lib/fonts.ts";
import type {
  HeaderFooterConfig,
  WatermarkConfig,
} from "../lib/protocol.ts";
import { MM_TO_PT } from "../lib/ui-constants.ts";
import { ColorPicker } from "./ColorPicker.tsx";

/** Headers / footers / page numbers / watermark sidebar. Each Apply
 *  burns its section into the page content via the decorate op — one
 *  rebase, undoable, and the panel stays open for iteration. */
export function DecorPanel({
  currentPage,
  pageCount,
  busy,
  customFonts,
  onApply,
  onClose,
  paneWidth,
}: {
  currentPage: number;
  pageCount: number;
  busy: boolean;
  customFonts: CustomFont[];
  onApply: (cfg: { headerFooter?: HeaderFooterConfig; watermark?: WatermarkConfig }) => void;
  onClose: () => void;
  paneWidth: number;
}) {
  const [slots, setSlots] = useState({ hl: "", hc: "", hr: "", fl: "", fc: "Page {page} of {pages}", fr: "" });
  const [hfFamily, setHfFamily] = useState("");
  const [wmFamily, setWmFamily] = useState("");
  const [hfSize, setHfSize] = useState(9);
  const [hfColor, setHfColor] = useState("#666666");
  const [marginXMm, setMarginXMm] = useState(18);
  const [marginYMm, setMarginYMm] = useState(12);
  const [hfTarget, setHfTarget] = useState<"all" | "notFirst" | "current">("all");
  const [wmText, setWmText] = useState("DRAFT");
  const [wmSize, setWmSize] = useState(64);
  const [wmColor, setWmColor] = useState("#e03131");
  const [wmOpacity, setWmOpacity] = useState(0.15);
  const [wmAngle, setWmAngle] = useState(45);
  const [wmBehind, setWmBehind] = useState(false);
  const [wmTarget, setWmTarget] = useState<"all" | "notFirst" | "current">("all");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  /** Font bytes for a family id ("" = Helvetica). Fetch failures fall
   *  back to Helvetica rather than blocking the apply. */
  const resolveFace = async (
    famId: string
  ): Promise<{ name: string; bytes: ArrayBuffer } | undefined> => {
    try {
      if (isLibraryFamily(famId)) {
        const lib = libraryEntry(famId);
        if (!lib) return undefined;
        const v = pickVariant(lib, false, false);
        return {
          name: `${lib.label.replace(/\s+/g, "")}-${v.variant}`,
          bytes: await fetchFontBytes(v.url),
        };
      }
      const cf = customFonts.find((c) => c.id === famId);
      return cf
        ? {
            name: cf.label.replace(/[^A-Za-z0-9-]/g, "") || "Uploaded",
            bytes: cf.bytes.slice(0),
          }
        : undefined;
    } catch {
      return undefined;
    }
  };

  const fontSelect = (value: string, set: (v: string) => void) => (
    <select value={value} onChange={(e) => set(e.target.value)} className={`${fieldCls} flex-1`}>
      <option value="">Helvetica</option>
      <optgroup label="Library">
        {FONT_LIBRARY.map((f) => (
          <option key={f.id} value={`lib:${f.id}`}>
            {f.label}
          </option>
        ))}
      </optgroup>
      {customFonts.length > 0 && (
        <optgroup label="Uploaded">
          {customFonts.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );

  const resolveTarget = (t: "all" | "notFirst" | "current") =>
    t === "current" ? currentPage : t;
  const anySlot = Object.values(slots).some((s) => s.trim().length > 0);
  const fieldCls =
    "h-7 w-full min-w-0 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-1.5 text-xs";
  const labelCls = "w-14 shrink-0 text-[11px] text-[color:var(--color-fg-muted)]";

  const targetSelect = (
    value: "all" | "notFirst" | "current",
    set: (v: "all" | "notFirst" | "current") => void
  ) => (
    <select value={value} onChange={(e) => set(e.target.value as never)} className={`${fieldCls} flex-1`}>
      <option value="all">All pages</option>
      <option value="notFirst">All except first</option>
      <option value="current">Page {currentPage + 1}</option>
    </select>
  );

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto bg-[color:var(--color-bg)]" style={{ width: paneWidth }}>
      <div className="flex flex-col gap-3 p-3">
        <div className="flex items-center gap-2">
          <i className="ri-layout-top-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
          <h2 className="text-sm font-semibold">Headers &amp; watermark</h2>
          <button
            type="button"
            title="Close"
            aria-label="Close headers panel"
            onClick={onClose}
            className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)]"
          >
            <i className="ri-close-line" aria-hidden />
          </button>
        </div>

        <div className="space-y-1.5 rounded-md border border-[color:var(--color-border)] p-2">
          <p className="text-xs font-medium">Header &amp; footer</p>
          {([
            ["Header", ["hl", "hc", "hr"]],
            ["Footer", ["fl", "fc", "fr"]],
          ] as const).map(([label, keys]) => (
            <div key={label} className="flex items-center gap-1">
              <span className={labelCls}>{label}</span>
              {keys.map((k) => (
                <input
                  key={k}
                  value={slots[k]}
                  onChange={(e) => setSlots((s) => ({ ...s, [k]: e.target.value }))}
                  placeholder={k.endsWith("l") ? "left" : k.endsWith("c") ? "centre" : "right"}
                  className={fieldCls}
                />
              ))}
            </div>
          ))}
          <p className="text-[10px] text-[color:var(--color-fg-muted)]">
            {"{page}"}, {"{pages}"} and {"{date}"} substitute per page — e.g. “Page {"{page}"} of {"{pages}"}”.
          </p>
          <div className="flex items-center gap-1">
            <span className={labelCls}>Font</span>
            {fontSelect(hfFamily, setHfFamily)}
          </div>
          <div className="flex items-center gap-1">
            <span className={labelCls}>Size / colour</span>
            <input
              type="number"
              min={6}
              max={24}
              value={hfSize}
              onChange={(e) => setHfSize(Number(e.target.value) || 9)}
              className={`${fieldCls} w-14 flex-none`}
            />
            <ColorPicker value={hfColor} onChange={setHfColor} title="Header/footer colour" />
          </div>
          <div className="flex items-center gap-1">
            <span className={labelCls}>Margins mm</span>
            <input
              type="number"
              min={4}
              max={60}
              title="From the side edges"
              value={marginXMm}
              onChange={(e) => setMarginXMm(Number(e.target.value) || 18)}
              className={`${fieldCls} w-14 flex-none`}
            />
            <input
              type="number"
              min={4}
              max={60}
              title="From the top/bottom edge"
              value={marginYMm}
              onChange={(e) => setMarginYMm(Number(e.target.value) || 12)}
              className={`${fieldCls} w-14 flex-none`}
            />
          </div>
          <div className="flex items-center gap-1">
            <span className={labelCls}>Apply to</span>
            {targetSelect(hfTarget, setHfTarget)}
          </div>
          <button
            type="button"
            disabled={busy || !anySlot}
            onClick={() =>
              void (async () =>
                onApply({
                  headerFooter: {
                    slots,
                    customFont: await resolveFace(hfFamily),
                    size: hfSize,
                    color: hfColor,
                    marginX: marginXMm * MM_TO_PT,
                    marginY: marginYMm * MM_TO_PT,
                    target: resolveTarget(hfTarget),
                  },
                }))()
            }
            className="inline-flex h-7 w-full items-center justify-center rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] text-xs text-[color:var(--color-accent-fg)] hover:opacity-90 disabled:opacity-40"
          >
            Apply header &amp; footer
          </button>
        </div>

        <div className="space-y-1.5 rounded-md border border-[color:var(--color-border)] p-2">
          <p className="text-xs font-medium">Watermark</p>
          <div className="flex items-center gap-1">
            <span className={labelCls}>Text</span>
            <input value={wmText} onChange={(e) => setWmText(e.target.value)} className={fieldCls} />
          </div>
          <div className="flex items-center gap-1">
            <span className={labelCls}>Font</span>
            {fontSelect(wmFamily, setWmFamily)}
          </div>
          <div className="flex items-center gap-1">
            <span className={labelCls}>Size / colour</span>
            <input
              type="number"
              min={12}
              max={200}
              value={wmSize}
              onChange={(e) => setWmSize(Number(e.target.value) || 64)}
              className={`${fieldCls} w-14 flex-none`}
            />
            <ColorPicker value={wmColor} onChange={setWmColor} title="Watermark colour" />
          </div>
          <div className="flex items-center gap-1">
            <span className={labelCls}>Opacity</span>
            <input
              type="range"
              min={5}
              max={100}
              value={Math.round(wmOpacity * 100)}
              onChange={(e) => setWmOpacity(Number(e.target.value) / 100)}
              className="min-w-0 flex-1"
            />
            <span className="w-8 text-right text-[11px] text-[color:var(--color-fg-muted)]">
              {Math.round(wmOpacity * 100)}%
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className={labelCls}>Angle</span>
            <select
              value={wmAngle}
              onChange={(e) => setWmAngle(Number(e.target.value))}
              className={`${fieldCls} flex-1`}
            >
              <option value={45}>Diagonal ↗ (45°)</option>
              <option value={-45}>Diagonal ↘ (−45°)</option>
              <option value={0}>Horizontal</option>
              <option value={90}>Vertical</option>
            </select>
          </div>
          <div className="flex items-center gap-3 text-[11px]">
            <span className={labelCls}>Placement</span>
            <label className="inline-flex cursor-pointer items-center gap-1">
              <input type="radio" checked={!wmBehind} onChange={() => setWmBehind(false)} />
              Over (transparent)
            </label>
            <label className="inline-flex cursor-pointer items-center gap-1">
              <input type="radio" checked={wmBehind} onChange={() => setWmBehind(true)} />
              Behind
            </label>
          </div>
          <div className="flex items-center gap-1">
            <span className={labelCls}>Apply to</span>
            {targetSelect(wmTarget, setWmTarget)}
          </div>
          <button
            type="button"
            disabled={busy || wmText.trim().length === 0}
            onClick={() =>
              void (async () =>
                onApply({
                  watermark: {
                    text: wmText,
                    customFont: await resolveFace(wmFamily),
                    size: wmSize,
                    color: wmColor,
                    opacity: wmOpacity,
                    angleDeg: wmAngle,
                    behind: wmBehind,
                    target: resolveTarget(wmTarget),
                  },
                }))()
            }
            className="inline-flex h-7 w-full items-center justify-center rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] text-xs text-[color:var(--color-accent-fg)] hover:opacity-90 disabled:opacity-40"
          >
            Apply watermark
          </button>
        </div>

        <p className="text-[10px] text-[color:var(--color-fg-muted)]">
          Applied to the document itself ({pageCount} page{pageCount === 1 ? "" : "s"} open) —
          undo with Ctrl+Z. “Behind” can hide under pages with a full-bleed background.
        </p>
      </div>
    </div>
  );
}
